import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import FullPreview from "../Preview/full-preview";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setConditionAttribute,
  setLabelAttribute,
  setDesignAttribute,
  setSizeMode,
  updateLabel,
  toggleOpenEditModal,
} from "../../../../redux/labels";

const SIZE_FIELDS = {
  desktop: {
    collection: ["label_width", "label_height"],
    product: ["label_width_product", "label_height_product"],
  },
  mobile: {
    collection: ["label_width_mobile", "label_height_mobile"],
    product: ["label_width_product_mobile", "label_height_product_mobile"],
  },
};

const FONT_SIZE_FIELDS = {
  desktop: { collection: "font_size", product: "font_size_product" },
  mobile: { collection: "font_size_mobile", product: "font_size_product_mobile" },
};

// The six square shapes are forced to 1:1 by both renderers (ONE_ONE_SHAPES in the SVG module,
// `aspect-ratio: 1/1` in the extension's label-badge.css), so a separate Height control has never
// done anything for them. Kept in sync with utility/svgLabelBadge.js in the admin app.
const ONE_ONE_SHAPES = [
  "pl-text-circle",
  "pl-text-star",
  "pl-text-trapezoid",
  "pl-text-trapezoid-bottom",
  "pl-text-triangle",
  "pl-text-triangle-bottom",
];

const SECTION_OPTIONS = [
  { value: "collection", label: "Collection section" },
  { value: "product", label: "Product section" },
  { value: "include_section", label: "Only in these selectors" },
  { value: "ignore_section", label: "Never in these selectors" },
];

// The template overrides MuiFormControlLabel root to `display: block`, which breaks the
// control/label alignment — restore the MUI default flex row for the controls below.
const CONTROL_LABEL_SX = {
  display: "flex",
  alignItems: "center",
  ml: 0,
  minHeight: 32,
  "& .MuiFormControlLabel-label": {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.4,
    transform: "none",
  },
};

// `condition.section` lists the enabled section rules; empty means no section filter at all.
const normalizeSection = (value) => (Array.isArray(value) ? value : []);

function SizeRow({ label, value, unit, onValueChange, onUnitChange, max }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Slider
          value={value ?? 0}
          onChange={(_, v) => onValueChange(v)}
          min={0}
          max={max}
          sx={{ flex: 1 }}
        />
        <TextField
          value={value ?? ""}
          onChange={(e) => onValueChange(Number(e.target.value))}
          type="number"
          size="small"
          sx={{ width: 70 }}
          inputProps={{ min: 0, max }}
        />
        <Select
          value={unit}
          onChange={(e) => onUnitChange(e.target.value)}
          sx={{ width: 60, height: 40 }}
        >
          <MenuItem value="%">%</MenuItem>
          <MenuItem value="px">px</MenuItem>
        </Select>
      </Stack>
    </Box>
  );
}

SizeRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  unit: PropTypes.string.isRequired,
  onValueChange: PropTypes.func.isRequired,
  onUnitChange: PropTypes.func.isRequired,
  max: PropTypes.number.isRequired,
};

function ModalEditLabelSize() {
  const dispatch = useDispatch();
  const shopDomain = useSelector((state) => state.shop.shopDomain);
  const svgLabelBadge = useSelector((state) => state.shop.svgLabelBadge);
  const openEditModal = useSelector((state) => state.label.openEditModal);
  const label = useSelector((state) => state.label.label);
  const design = useSelector((state) => state.label.design);
  const sizeMode = useSelector((state) => state.label.sizeMode);

  const [device, setDevice] = useState(0);
  const [page, setPage] = useState("collection");
  const [saving, setSaving] = useState(false);
  const [includeText, setIncludeText] = useState("");
  const [ignoreText, setIgnoreText] = useState("");
  const [sectionMode, setSectionMode] = useState("all");
  const [tab, setTab] = useState(0);

  // Fill the section controls from a label record; no section data means "All sections".
  const syncSectionForm = (data) => {
    const rules = normalizeSection(data?.conditions?.section);
    setSectionMode(rules.length ? "select" : "all");
    setIncludeText(
      rules.includes("include_section") ? data?.conditions?.include_sections ?? "" : ""
    );
    setIgnoreText(rules.includes("ignore_section") ? data?.conditions?.ignore_sections ?? "" : "");
  };

  useEffect(() => {
    if (!label?.id) return;
    setDevice(0);
    setPage("collection");
    setTab(0);
    syncSectionForm(label);
  }, [label?.id]);

  if (!label?.id) return null;

  const isImageLabel = label.label_type === 0;

  // "Same size on all screens" stores exactly one value, so it always reads and writes the base
  // fields — never the per-screen ones, which stay null in that mode.
  const deviceKey = sizeMode === "same" ? "desktop" : device === 0 ? "desktop" : "mobile";
  const pageKey = sizeMode === "same" ? "collection" : page;
  const [widthField, heightField] = SIZE_FIELDS[deviceKey][pageKey];
  const fontSizeField = FONT_SIZE_FIELDS[deviceKey][pageKey];
  const widthUnit = label.fixed_size ? "px" : "%";
  const maxSize = label.fixed_size ? 800 : 150;

  // A square shape shows no Height control, so mirror whatever width field is being written into
  // the matching height field. Both renderers draw these square regardless, but leaving height at
  // a stale value would make the stored data describe a shape it never renders.
  const squareShape = !label.image && ONE_ONE_SHAPES.includes(design?.shape_class);
  // One dimension only: that, or an image label with "Lock aspect ratio".
  const aspectLocked = (label.ratio_size && label.image) || squareShape;

  // Per-screen values fall back to the next-broader one, mirroring the admin app: product+mobile →
  // product → base, collection+mobile → base.
  const readSize = (dimension) => {
    const base = label[`label_${dimension}`];
    if (sizeMode === "same") return base;
    if (pageKey === "collection") {
      return deviceKey === "desktop" ? base : label[`label_${dimension}_mobile`] ?? base;
    }
    const product = label[`label_${dimension}_product`] ?? base;
    return deviceKey === "desktop"
      ? product
      : label[`label_${dimension}_product_mobile`] ?? product;
  };

  const currentWidth = readSize("width") ?? 30;
  const currentHeight = readSize("height") ?? 10;

  const currentFontSize =
    (sizeMode === "same"
      ? design.font_size
      : pageKey === "collection"
      ? deviceKey === "desktop"
        ? design.font_size
        : design.font_size_mobile ?? design.font_size
      : deviceKey === "desktop"
      ? design.font_size_product ?? design.font_size
      : design.font_size_product_mobile ?? design.font_size_product ?? design.font_size) ?? 12;

  // In "same" mode the base field is written and every per-screen field is cleared, so a later load
  // cannot see a half-populated per-screen key and silently flip the mode back to "custom".
  const applyWidth = (value) => {
    const v = value > 0 ? value : 0;
    const withHeight = (attrs) => {
      if (!squareShape) return attrs;
      const out = { ...attrs };
      Object.keys(attrs).forEach((key) => {
        out[key.replace("_width", "_height")] = attrs[key];
      });
      return out;
    };
    dispatch(
      setLabelAttribute(
        withHeight(
          sizeMode === "same"
            ? {
                label_width: v,
                label_width_mobile: null,
                label_width_product: null,
                label_width_product_mobile: null,
              }
            : { [widthField]: v }
        )
      )
    );
  };

  const applyHeight = (value) => {
    const v = value > 0 ? value : 0;
    dispatch(
      setLabelAttribute(
        sizeMode === "same"
          ? {
              label_height: v,
              label_height_mobile: null,
              label_height_product: null,
              label_height_product_mobile: null,
            }
          : { [heightField]: v }
      )
    );
  };

  const applyFontSize = (value) => {
    const v = value > 0 ? value : 0;
    dispatch(
      setDesignAttribute(
        sizeMode === "same"
          ? {
              font_size: v,
              font_size_mobile: null,
              font_size_product: null,
              font_size_product_mobile: null,
            }
          : { [fontSizeField]: v }
      )
    );
  };

  const handleChangeUnit = (value) => dispatch(setLabelAttribute({ fixed_size: value === "px" }));

  const sectionRules = normalizeSection(label.conditions?.section);

  const handleChangeSectionMode = (mode) => {
    setSectionMode(mode);
    if (mode === "all") {
      setIncludeText("");
      setIgnoreText("");
      dispatch(
        setConditionAttribute({
          section: [],
          include_sections: "",
          ignore_sections: "",
        })
      );
    }
  };

  const handleToggleSection = (value, checked) => {
    const selected = checked
      ? [...sectionRules, value]
      : sectionRules.filter((item) => item !== value);
    // Unticking a selector rule drops its text too, so a disabled rule cannot leave a
    // stale selector behind in the DB.
    const cleared = {};
    if (!selected.includes("include_section")) {
      setIncludeText("");
      cleared.include_sections = "";
    }
    if (!selected.includes("ignore_section")) {
      setIgnoreText("");
      cleared.ignore_sections = "";
    }
    dispatch(setConditionAttribute({ section: selected, ...cleared }));
  };

  const handleChangeIncludeSections = (value) => {
    setIncludeText(value);
    dispatch(setConditionAttribute({ include_sections: value }));
  };

  const handleChangeIgnoreSections = (value) => {
    setIgnoreText(value);
    dispatch(setConditionAttribute({ ignore_sections: value }));
  };

  const handleChangeSizeMode = (mode) => {
    dispatch(setSizeMode(mode));
    if (mode === "same") {
      dispatch(
        setLabelAttribute({
          label_width_mobile: null,
          label_height_mobile: null,
          label_width_product: null,
          label_height_product: null,
          label_width_product_mobile: null,
          label_height_product_mobile: null,
        })
      );
      dispatch(
        setDesignAttribute({
          font_size_mobile: null,
          font_size_product: null,
          font_size_product_mobile: null,
        })
      );
    } else {
      // Seed every per-screen field from the base value so no screen starts at null/0.
      dispatch(
        setLabelAttribute({
          label_width_mobile: label.label_width,
          label_width_product: label.label_width,
          label_width_product_mobile: label.label_width,
          label_height_mobile: label.label_height,
          label_height_product: label.label_height,
          label_height_product_mobile: label.label_height,
        })
      );
      dispatch(
        setDesignAttribute({
          font_size_mobile: design.font_size,
          font_size_product: design.font_size,
          font_size_product_mobile: design.font_size,
        })
      );
    }
  };

  const handleSave = () => {
    setSaving(true);

    // "Select sections" with nothing ticked means no section filter — save it as
    // "All sections" and reflect that back in the form.
    const hasSection = sectionMode === "select" && sectionRules.length > 0;
    if (!hasSection && sectionMode !== "all") {
      setSectionMode("all");
      setIncludeText("");
      setIgnoreText("");
    }

    // Section display lives on the condition, not on the label itself.
    const conditionPayload = {
      section: hasSection ? sectionRules : [],
      include_sections:
        hasSection && sectionRules.includes("include_section")
          ? label.conditions?.include_sections ?? ""
          : "",
      ignore_sections:
        hasSection && sectionRules.includes("ignore_section")
          ? label.conditions?.ignore_sections ?? ""
          : "",
    };

    const labelPayload =
      sizeMode === "same"
        ? {
            label_width: label.label_width,
            label_height: label.label_height,
            label_width_mobile: null,
            label_height_mobile: null,
            label_width_product: null,
            label_height_product: null,
            label_width_product_mobile: null,
            label_height_product_mobile: null,
            auto_responsive: label.auto_responsive,
            responsive_ratio: label.responsive_ratio,
            ratio_size: label.ratio_size,
            fixed_size: label.fixed_size,
          }
        : {
            label_width: label.label_width,
            label_height: label.label_height,
            label_width_mobile: label.label_width_mobile,
            label_height_mobile: label.label_height_mobile,
            label_width_product: label.label_width_product,
            label_height_product: label.label_height_product,
            label_width_product_mobile: label.label_width_product_mobile,
            label_height_product_mobile: label.label_height_product_mobile,
            auto_responsive: label.auto_responsive,
            responsive_ratio: label.responsive_ratio,
            ratio_size: label.ratio_size,
            fixed_size: label.fixed_size,
          };

    const designPayload = isImageLabel
      ? {}
      : // `auto_responsive` drives the text size on the legacy renderer, so the per-screen font
      // sizes are irrelevant there. On the SVG branch the flag is dead (the viewBox scales the
      // text) and a stale `auto_responsive = 1` must not swallow the sizes just edited.
      !svgLabelBadge && label.auto_responsive
      ? { font_size: design.font_size, letter_spacing: design.letter_spacing }
      : sizeMode === "same"
      ? {
          font_size: design.font_size,
          letter_spacing: design.letter_spacing,
          font_size_mobile: null,
          font_size_product: null,
          font_size_product_mobile: null,
        }
      : {
          font_size: design.font_size,
          font_size_mobile: design.font_size_mobile ?? design.font_size,
          font_size_product: design.font_size_product ?? design.font_size,
          font_size_product_mobile: design.font_size_product_mobile ?? design.font_size,
          letter_spacing: design.letter_spacing,
        };

    const shop = shopDomain.replace(".myshopify.com", "").replace("https://", "");
    dispatch(
      updateLabel({
        id: label.id,
        data: { label: labelPayload, condition: conditionPayload, design: designPayload, shop },
      })
    )
      .unwrap()
      .then((response) => {
        // The API response is the source of truth — re-fill the form from it rather than
        // from the payload we sent.
        const saved = response?.data ?? response;
        syncSectionForm(saved);
      })
      .catch(() => {})
      .finally(() => {
        setSaving(false);
        dispatch(toggleOpenEditModal());
      });
  };

  return (
    <Modal open={openEditModal} onClose={() => dispatch(toggleOpenEditModal())}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 860,
          maxWidth: "95vw",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          Edit — {label.name}
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Design" />
          <Tab label="Display" />
        </Tabs>

        <Stack direction="row" spacing={3}>
          <Box sx={{ width: 280, flexShrink: 0 }}>
            <FullPreview
              data={label}
              type="label"
              design={design}
              typePage={page}
              device={deviceKey}
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ flex: 1, overflowY: "auto", maxHeight: "70vh", px: "10px" }}>
            <Stack spacing={2}>
              {tab === 0 && (
                <>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Size mode
                    </Typography>
                    <Select
                      fullWidth
                      value={sizeMode}
                      onChange={(e) => handleChangeSizeMode(e.target.value)}
                      sx={{ height: 40 }}
                    >
                      <MenuItem value="same">Same size on all screens</MenuItem>
                      <MenuItem value="custom">Custom size per screen</MenuItem>
                    </Select>
                  </Box>

                  {sizeMode === "custom" && (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Tabs value={device} onChange={(_, v) => setDevice(v)} sx={{ minHeight: 32 }}>
                        <Tab label="Desktop" sx={{ minHeight: 32, py: 0.5 }} />
                        <Tab label="Mobile" sx={{ minHeight: 32, py: 0.5 }} />
                      </Tabs>
                      <Select
                        value={page}
                        onChange={(e) => setPage(e.target.value)}
                        sx={{ height: 40 }}
                      >
                        <MenuItem value="collection">Collection page</MenuItem>
                        <MenuItem value="product">Product page</MenuItem>
                      </Select>
                    </Stack>
                  )}

                  <SizeRow
                    label={aspectLocked ? "Size" : "Width"}
                    value={currentWidth}
                    unit={widthUnit}
                    onValueChange={applyWidth}
                    onUnitChange={handleChangeUnit}
                    max={maxSize}
                  />
                  {!aspectLocked && (
                    <SizeRow
                      label="Height"
                      value={currentHeight}
                      unit={widthUnit}
                      onValueChange={applyHeight}
                      onUnitChange={handleChangeUnit}
                      max={maxSize}
                    />
                  )}

                  {isImageLabel ? (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!label.ratio_size}
                          onChange={(e) =>
                            dispatch(setLabelAttribute({ ratio_size: e.target.checked }))
                          }
                        />
                      }
                      label="Lock aspect ratio"
                      sx={CONTROL_LABEL_SX}
                    />
                  ) : (
                    <>
                      {/* Auto-adjust only exists on the legacy renderer. On the SVG branch the
                          logical viewBox already scales the text with the label, so the toggle and
                          its ratio slider would do nothing — hide them rather than leave dead
                          controls on screen. The DB fields stay untouched so legacy shops keep
                          working. See .claude/features/svg_label_and_badge.md §4.7 */}
                      {!svgLabelBadge && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!label.auto_responsive}
                              onChange={(e) =>
                                dispatch(setLabelAttribute({ auto_responsive: e.target.checked }))
                              }
                            />
                          }
                          label="Auto-adjust text size"
                          sx={CONTROL_LABEL_SX}
                        />
                      )}

                      {!svgLabelBadge && label.auto_responsive ? (
                        <Box>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            Text size (% background height)
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Slider
                              value={Math.round((label.responsive_ratio ?? 0.6) * 100)}
                              onChange={(_, v) =>
                                dispatch(setLabelAttribute({ responsive_ratio: v / 100 }))
                              }
                              min={0}
                              max={100}
                              sx={{ flex: 1 }}
                            />
                            <Typography variant="body2" sx={{ minWidth: 30, textAlign: "right" }}>
                              {Math.round((label.responsive_ratio ?? 0.6) * 100)}
                            </Typography>
                          </Stack>
                        </Box>
                      ) : (
                        <Box>
                          {/* On the SVG branch the number is a share of the background height, not
                              pixels. That goes in the LABEL rather than as a unit suffix: "%" on
                              its own reads as "percent of what?", and this is the only place a
                              merchant learns the unit. */}
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {svgLabelBadge ? "Text size (% background height)" : "Text size"}
                          </Typography>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Slider
                              value={currentFontSize}
                              onChange={(_, v) => applyFontSize(v)}
                              min={0}
                              max={100}
                              sx={{ flex: 1 }}
                            />
                            {svgLabelBadge ? (
                              // Slider only on the SVG branch: the number is a percentage of the
                              // background height (see the label), so a free-text box invites values
                              // the slider cannot reach and gives nothing the handle does not.
                              // Matches Space between letters.
                              <Typography variant="body2" sx={{ minWidth: 30, textAlign: "right" }}>
                                {currentFontSize}
                              </Typography>
                            ) : (
                              <>
                                <TextField
                                  value={currentFontSize}
                                  onChange={(e) => applyFontSize(Number(e.target.value))}
                                  type="number"
                                  size="small"
                                  sx={{ width: 70 }}
                                />
                                <Typography variant="body2">px</Typography>
                              </>
                            )}
                          </Stack>
                        </Box>
                      )}

                      <Box>
                        {/* Same unit as font size on the SVG branch: letter-spacing is in logical
                            viewBox units and the viewBox is a fixed 100 units tall, so one unit is
                            one percent of the background height. Unit stated in the label, not as a
                            "px" suffix that would be plain wrong. */}
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {svgLabelBadge
                            ? "Space between letters (% background height)"
                            : "Space between letters"}
                        </Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Slider
                            value={design.letter_spacing ?? 0}
                            onChange={(_, v) => dispatch(setDesignAttribute({ letter_spacing: v }))}
                            min={0}
                            max={100}
                            sx={{ flex: 1 }}
                          />
                          <Typography variant="body2" sx={{ minWidth: 40, textAlign: "right" }}>
                            {svgLabelBadge
                              ? design.letter_spacing ?? 0
                              : `${design.letter_spacing ?? 0}px`}
                          </Typography>
                        </Stack>
                      </Box>
                    </>
                  )}
                </>
              )}

              {tab === 1 && (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    Display section
                  </Typography>
                  <RadioGroup
                    value={sectionMode}
                    onChange={(e) => handleChangeSectionMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label="All sections"
                      sx={CONTROL_LABEL_SX}
                    />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <FormControlLabel
                        value="select"
                        control={<Radio size="small" />}
                        label="Select sections"
                        sx={CONTROL_LABEL_SX}
                      />
                    </Stack>
                  </RadioGroup>

                  {sectionMode === "select" && (
                    <Box
                      sx={{
                        mt: 1,
                        ml: 1.5,
                        pl: 2,
                        borderLeft: "2px solid",
                        borderColor: "grey.300",
                      }}
                    >
                      <Stack spacing={2}>
                        <Stack>
                          {SECTION_OPTIONS.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={sectionRules.includes(option.value)}
                                  onChange={(e) =>
                                    handleToggleSection(option.value, e.target.checked)
                                  }
                                />
                              }
                              label={option.label}
                              sx={CONTROL_LABEL_SX}
                            />
                          ))}
                        </Stack>

                        {sectionRules.includes("include_section") && (
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            size="small"
                            label="Only in these selectors"
                            helperText="A CSS selector list, comma separated. Max 4000 characters."
                            placeholder=".product-grid, #shopify-section-featured"
                            value={includeText}
                            onChange={(e) => handleChangeIncludeSections(e.target.value)}
                            inputProps={{ maxLength: 4000 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}

                        {sectionRules.includes("ignore_section") && (
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            size="small"
                            label="Never in these selectors"
                            helperText="Takes priority over the include list. Max 4000 characters."
                            placeholder=".related-products, #shopify-section-footer"
                            value={ignoreText}
                            onChange={(e) => handleChangeIgnoreSections(e.target.value)}
                            inputProps={{ maxLength: 4000 }}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </Stack>
                    </Box>
                  )}
                </Box>
              )}

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ pt: 1, pb: 1 }}>
                <Button onClick={() => dispatch(toggleOpenEditModal())} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="contained" color="info" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
}

export default ModalEditLabelSize;
